import { Button } from "@/components/ui/Button";
import H1 from "@/components/ui/H1";
import P from "@/components/ui/P";
import { CircleCheck } from "lucide-react";
import { Link, useSearchParams, useNavigate } from "react-router";
import PartyImg from "@/res/images/party-illustration.png";
import { OrderStatus } from "@/api/SolarCloudApi";
import LoadingSection from "@/components/LoadingSection";
import { useBuyPanelsStore } from "../store";
import { useEffect, useRef, useState } from "react";
import { Progress } from "@/components/ui/progress";
import { useProfileContext } from "@/contexts/ProfileContext";
import { useUser } from "@/queries/user/useUser";
import { useUsersOrders } from "@/queries/order/useUsersOrders";
import { useNewOrder } from "@/queries/order/useNewOrder";
import { useSetOrderUuid, invalidateOrderUuidStore } from "@/stores/orderIdStore";
import { useOutstandingChecks } from "@/hooks/useOutstandingChecks";
import { useCreateNewOrder } from "@/queries/order/useCreateNewOrder";
import { findDefaultEmail } from "@/util/user";
import { usePixelContext } from "@/contexts/MetaPixelContext";
import { TrackableEventNameEnum } from "react-meta-pixel";

// export enum OrderStatus {
//     DRAFT = 'Draft',
//     SUBMITTED = 'Submitted',
//     DEPOSIT_TAKEN = 'DepositTaken',
//     HOLD_TAKEN = 'HoldTaken',
//     PART_ALLOCATED = 'PartAllocated',
//     ALLOCATED = 'Allocated',
//     PAYMENT_PENDING = 'PaymentPending',
//     PARTIAL_PAYMENT = 'PartialPayment',
//     PAID_IN_FULL = 'PaidInFull',
//     PARTIAL_FULFILLMENT = 'PartialFulfillment',
//     FULFILLED = 'Fulfilled',
//     CANCEL_PENDING = 'CancelPending',
//     CANCELED = 'Canceled',
// }

const progressMap: Record<OrderStatus, number> = {
  [OrderStatus.DEPOSIT_TAKEN]: 40,
  [OrderStatus.HOLD_TAKEN]: 70,
  [OrderStatus.FULFILLED]: 100,
  [OrderStatus.PAID_IN_FULL]: 100,
  [OrderStatus.PARTIAL_FULFILLMENT]: 100,
  [OrderStatus.PARTIAL_PAYMENT]: 100,
  [OrderStatus.PAYMENT_PENDING]: 20,
  [OrderStatus.PAYMENT_FAILED]: 0,
  [OrderStatus.SUBMITTED]: 10,
  [OrderStatus.CANCEL_PENDING]: 0,
  [OrderStatus.CANCELED]: 0,
  [OrderStatus.DRAFT]: 0,
  [OrderStatus.PART_ALLOCATED]: 60,
  [OrderStatus.ALLOCATED]: 80,
};

const statusMap: Record<OrderStatus, string> = {
  [OrderStatus.DEPOSIT_TAKEN]: "Deposit Taken",
  [OrderStatus.HOLD_TAKEN]: "Hold Taken",
  [OrderStatus.FULFILLED]: "Complete",
  [OrderStatus.PAID_IN_FULL]: "Paid in Full",
  [OrderStatus.PARTIAL_FULFILLMENT]: "Partial Fulfillment",
  [OrderStatus.PARTIAL_PAYMENT]: "Partial Payment",
  [OrderStatus.PAYMENT_PENDING]: "Payment Pending",
  [OrderStatus.PAYMENT_FAILED]: "Payment Failed",
  [OrderStatus.SUBMITTED]: "Submitted",
  [OrderStatus.CANCEL_PENDING]: "Cancel Pending",
  [OrderStatus.CANCELED]: "Canceled",
  [OrderStatus.DRAFT]: "Draft",
  [OrderStatus.PART_ALLOCATED]: "Part Allocated",
  [OrderStatus.ALLOCATED]: "Allocated",
};

export default function OrderConfirmation() {
  const hasTrackedRef = useRef(false);
  const { pixel } = usePixelContext();
  const { authentication } = useProfileContext();
  const { data: user } = useUser();
  const [searchParams] = useSearchParams();
  const orderUuid = searchParams.get("order") ?? "";
  const reset = useBuyPanelsStore((state) => state.reset);
  const navigate = useNavigate();
  const setOrderUuid = useSetOrderUuid();
  const {
    hasAnyOutstanding,
    hasOutstandingTerms,
    hasOutstandingAllocations,
    unacceptedTermsCount,
    unallocatedElectricityPanels,
  } = useOutstandingChecks();

  const [newOrderReady, setNewOrderReady] = useState(false);
  const { mutate: createNewOrder } = useCreateNewOrder({
    mutationConfig: {
      onSuccess: (data) => {
        if (data.uuid) {
          setNewOrderReady(true);
        }
      },
    },
  });

  useEffect(() => {
    // Reset the buy panels store and cart when the component mounts

    const precreateKey = `confirmation:precreated-next-order:${orderUuid || "unknown"}`;
    const alreadyPrecreated = typeof window !== "undefined" && window.sessionStorage.getItem(precreateKey) === "1";
    setOrderUuid("");
    try {
      invalidateOrderUuidStore();
    } catch (e) { }
    reset();

    setNewOrderReady(false);

    if (!alreadyPrecreated) {
      createNewOrder(undefined);
      try {
        window.sessionStorage.setItem(precreateKey, "1");
        window.sessionStorage.setItem(`confirmation:processed:${orderUuid || "unknown"}`, Date.now().toString());
      } catch (e) { }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderUuid]);

  const {
    data: orderData,
    isLoading,
    isRefetching,
  } = useUsersOrders({
    queryConfig: {
      staleTime: 0,
    },
  });
  // const { data: theOrder } = useOrder(orderUuid, {
  //   queryConfig: {
  //     enabled: !!orderUuid,
  //   },
  // });

  // Try direct fetch for the specific order UUID to avoid race conditions
  const { data: theOrder, isLoading: isOrderLoading } = useNewOrder({
    uuid: orderUuid,
    queryConfig: {
      enabled: !!orderUuid,
      staleTime: 0,
      refetchInterval: (data: any) =>
        data && (data.status === OrderStatus.SUBMITTED || data.status === OrderStatus.PAYMENT_PENDING) ? 2000 : false,
    },
  });

  const order = theOrder ?? orderData?.find((o) => o.uuid === orderUuid);
  const authEmail = authentication?.loggedIn ? authentication.email : undefined;
  const email = findDefaultEmail(user)?.address ?? authEmail ?? "";

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (!order || !orderUuid) return;

    const conversionEligibleStatuses: ReadonlyArray<OrderStatus> = [
      OrderStatus.DEPOSIT_TAKEN,
      OrderStatus.HOLD_TAKEN,
      OrderStatus.PAID_IN_FULL,
      OrderStatus.FULFILLED,
    ];

    const status = order.status;
    if (!status) return;

    if (!conversionEligibleStatuses.includes(status)) return;

    const storageKey = `confirmation:purchase-tracked:${orderUuid}`;
    const legacyKey = `confirmation:google-ads-conversion-fired:${orderUuid}`;
    try {
      if (
        hasTrackedRef.current ||
        localStorage.getItem(storageKey) === "1" ||
        sessionStorage.getItem(storageKey) === "1" ||
        sessionStorage.getItem(legacyKey) === "1"
      ) {
        return;
      }
    } catch {
      if (hasTrackedRef.current) return;
    }

    hasTrackedRef.current = true;

    // --- Format Items for GA4 / Meta ---
    const ga4Items = (order.items ?? []).map((item) => {
      const panelSize = item.product?.wattPanelSize || 100;
      const quantity = (item.units ?? 0) / panelSize;
      const price =
        item.itemTotal && quantity > 0
          ? Number((item.itemTotal / quantity).toFixed(2))
          : item.unitPrice ?? item.product?.wattPriceCurrent ?? 0;

      return {
        item_id: item.product?.uuid ?? item.uuid ?? "unknown",
        item_name: item.product?.name || "Solar Panel",
        price,
        quantity,
      };
    });

    // --- 1. Meta (Facebook) Pixel Purchase Event ---
    if (pixel) {
      const contents = order
        .items?.filter(
          (item): item is { product: { uuid: string }; units?: number } =>
            !!item.product?.uuid
        )
        .map((item) => ({
          id: item.product.uuid,
          quantity: (item.units ?? 0) / 100,
        })) ?? [];

      const num_items = contents.reduce(
        (total, item) => total + item.quantity,
        0
      );

      pixel.trackEvent(TrackableEventNameEnum.Purchase, {
        currency: "AUD",
        value: order.totalPrice ?? 0,
        contents,
        num_items,
      });
    }

    // --- 2. Standard GA4 / GTM Ecommerce Purchase Event ---
    if (!window.dataLayer) {
      window.dataLayer = [];
    }

    // Clear previous ecommerce object to prevent state leakage
    window.dataLayer.push({ ecommerce: null });

    window.dataLayer.push({
      event: "purchase",
      ecommerce: {
        transaction_id: String(order.orderNumber ?? order.uuid ?? orderUuid),
        value: order.totalPrice ?? 0,
        tax: order.totalTax ?? 0,
        currency: "AUD",
        items: ga4Items,
      },
    });

    // --- 3. Google Ads Conversion Event ---
    const conversionParams = {
      send_to: "AW-924643993/NstqCMyJl-8bEJnl87gD",
      value: order.totalPrice ?? 1.0,
      currency: "AUD",
      transaction_id: String(order.orderNumber ?? orderUuid),
      // new_customer: true, // calculate dynamically, populate with true/false
    };

    const trackedWindow = window as Window & {
      gtag?: (command: "event", eventName: string, params: typeof conversionParams) => void;
    };

    if (typeof trackedWindow.gtag === "function") {
      trackedWindow.gtag("event", "conversion", conversionParams);
    } else {
      window.dataLayer.push({
        event: "conversion",
        ...conversionParams,
      });
    }

    try {
      localStorage.setItem(storageKey, "1");
      sessionStorage.setItem(storageKey, "1");
      sessionStorage.setItem(legacyKey, "1");
    } catch (e) {
      // Ignore storage failures in private browsing modes.
    }
  }, [order, orderUuid, pixel]);

  if (isOrderLoading || isLoading || isRefetching) {
    return <LoadingSection />;
  }

  const orderStatus = order?.status;
  const progress = progressMap[orderStatus!] ?? 0;

  return (
    <>
      {!order && (
        <div className="mx-auto max-w-screen-sm p-2 py-20 space-y-4">
          <H1>Order not found</H1>
          <P>
            We couldn't find the order you are looking for. Please check the
            order UUID or contact support if you believe this is an error.
          </P>
        </div>
      )}
      {order && (
        <>
          <section className="mx-auto max-w-screen-sm p-2 space-y-4">
            <div className="flex items-center gap-2">
              <CircleCheck className="h-5 w-5 text-brand-blue-300" />
              <h2 className="text-brand-blue-300 text-base font-bold tracking-tight">
                Order Successful!
              </h2>
            </div>
            <h1 className="font-bold text-2xl text-brand-orange-500">
              Energy savings coming your way!
            </h1>
            <p className="text-base font-normal tracking-tight">
              {statusMap[orderStatus!]} —{" "}
              <span className="font-normal">Order #{order.orderNumber}</span>
            </p>
            {/* <p className="text-base font-semibold tracking-tight">
        {statusMap[orderStatus!]}
      </p> */}
            <Progress value={progress} className="h-4" />
            <ol className="flex gap-2 justify-around text-center text-sm divide-x divide-brand-blue-300/20 text-brand-blue-300/85">
              <li className="flex-1">Order Placed</li>
              <li className="flex-1">Deposit Received</li>
            </ol>
          </section>
          <section className="mx-auto max-w-screen-sm p-2 space-y-4 mt-4">
            <P>
              Welcome to the SolarCloud community. We appreciate your support of
              our innovative Australian renewable energy savings solution
            </P>
          </section>
          <section className="mx-auto max-w-screen-sm p-2 space-y-4 mt-10">
            <div className="flex flex-col sm:flex-row items-start justify-between gap-4 relative">
              <div className="space-y-2 flex-1">
                <p className="font-semibold leading-none">
                  You’ll receive the following shortly:
                </p>
                <ol className="text-base list-disc pl-5">
                  <li>Payment receipt</li>
                  <li>PDS document</li>
                  <li>Next steps email</li>
                  <li>Message from our CEO</li>
                </ol>
                <p className="text-base ">
                  We'll send everything to: <br />
                  {email || "your email."}
                </p>
              </div>

              <div className="w-48 md:w-60 flex-shrink-0 static">
                <img
                  src={PartyImg}
                  alt="Successful Order"
                  className="w-full h-auto object-contain"
                />
              </div>
            </div>
          </section>
        </>
      )}
      <div className="mt-4 p-2 mx-auto max-w-screen-sm">
        {/* {hasOutstandingTerms ? (
          <p className="font-semibold leading-none">
            Complete your setup by reviewing our Terms and Conditions
          </p>
        ) : hasOutstandingAllocations ? (
          <p className="font-semibold leading-none">
            Complete your setup by linking your Energy Account
          </p>
        ) : null} */}
        {/* <p className="font-semibold leading-none">Click the below button</p> */}
      </div>
      <div className="flex flex-col-reverse md:flex-row justify-between mx-auto max-w-screen-sm p-2 space-y-4 mt-10">
        {/* <OrderProceedButton enabled={newOrderReady} /> */}
        <Button className=" px-10">
          {/* {hasOutstandingAllocations ? (
            <Link to="/dashboard/allocations/electricity">
              Link my Energy Account
            </Link>
          ) : hasOutstandingTerms ? (
            <Link to="/dashboard/terms">Continue to Terms</Link>
          ) : ( */}
          <Link to="/dashboard">Go to Dashboard</Link>
          {/* )} */}
        </Button>
      </div>
    </>
  );
}
